/**
 * Painel — tela inicial (`/`), alimentada por `GET /dashboard`. Responde
 * "como estão as coisas e o que eu faço agora": saudação + ação principal,
 * os números do mês em tipografia (não cartões), as últimas propostas, o
 * ranking de equipamentos e os dois avisos específicos do negócio —
 * reajustes recentes e preços já agendados para o futuro.
 *
 * Deliberadamente sem gráfico: o único gráfico do sistema é a barra
 * empilhada de Nova Proposta. Aqui tudo é leitura direta.
 */
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useDashboard } from '@/hooks/useAdmin';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/Button';
import { Money } from '@/components/Money';
import { Percent } from '@/components/Percent';
import { fmtDate, deltaTone } from '@/lib/format';
import type { DashboardData, DashboardPrecoAgendado, DashboardReajuste, DashboardTopProduto, ProposalListRow } from '@/lib/types';
import './Painel.css';

function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'Bom dia';
  if (hour >= 12 && hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

function toneClass(tone: 'positive' | 'attention' | 'neutral'): string | undefined {
  if (tone === 'attention') return 'reading-attention';
  if (tone === 'positive') return 'reading-positive';
  return undefined;
}

export function Painel() {
  const { user } = useAuth();
  const dashboardQuery = useDashboard();
  const navigate = useNavigate();

  if (!user) return null;

  const firstName = user.name.trim().split(/\s+/)[0];

  return (
    <>
      <PageHeader
        title={`${greetingFor(new Date())}, ${firstName}.`}
        meta="Veja como estão as propostas do mês e o que precisa da sua atenção."
        actions={
          <Link to="/nova">
            <Button>Nova proposta</Button>
          </Link>
        }
      />

      {dashboardQuery.isLoading ? (
        <div className="stack-lg">
          <Panel>
            <Spinner label="Carregando o painel…" />
          </Panel>
        </div>
      ) : dashboardQuery.isError || !dashboardQuery.data ? (
        <Panel>
          <EmptyState
            title="Não foi possível carregar o painel"
            description="Tente atualizar a página em instantes."
          />
        </Panel>
      ) : (
        <PainelConteudo data={dashboardQuery.data} onOpenProposta={(id) => navigate(`/propostas/${id}`)} />
      )}
    </>
  );
}

function PainelConteudo({
  data,
  onOpenProposta,
}: {
  data: DashboardData;
  onOpenProposta: (id: number) => void;
}) {
  return (
    <div className="stack-lg">
      <Panel>
        {data.propostasTotal === 0 ? (
          <EmptyState
            title="Nenhuma proposta ainda."
            description="Os números do mês aparecem aqui assim que a primeira proposta for emitida. A primeira leva menos de dois minutos."
            action={
              <Link to="/nova">
                <Button>Nova proposta</Button>
              </Link>
            }
          />
        ) : (
          <div className="painel-figures">
            <div className="painel-figure">
              <span className="painel-figure__value mono">{data.propostasMes}</span>
              <p className="painel-figure__label">propostas no mês</p>
            </div>
            <div className="painel-figure">
              <span className="painel-figure__value">
                <Money value={data.valorMes} />
              </span>
              <p className="painel-figure__label">valor no mês</p>
            </div>
            <div className="painel-figure">
              <span className="painel-figure__value">
                <Money value={data.ticketMedio} />
              </span>
              <p className="painel-figure__label">ticket médio</p>
            </div>
            <div className="painel-figure">
              <span className="painel-figure__value mono">{data.propostasTotal}</span>
              <p className="painel-figure__label">total de propostas</p>
            </div>
          </div>
        )}
      </Panel>

      <UltimasPropostasPanel rows={data.ultimasPropostas} onOpen={onOpenProposta} />

      <div className="painel-grid">
        <TopProdutosPanel items={data.topProdutos} />
        <ReajustesPanel items={data.reajustesRecentes} />
        <PrecosAgendadosPanel items={data.precosAgendados} />
      </div>
    </div>
  );
}

function UltimasPropostasPanel({
  rows,
  onOpen,
}: {
  rows: ProposalListRow[];
  onOpen: (id: number) => void;
}) {
  const columns: DataTableColumn<ProposalListRow>[] = [
    {
      key: 'code',
      header: 'Código',
      render: (row) => (
        <Link to={`/propostas/${row.id}`} className="mono" onClick={(e) => e.stopPropagation()}>
          {row.code}
        </Link>
      ),
    },
    { key: 'cliente', header: 'Cliente', render: (row) => row.clientRazaoSocial },
    { key: 'uf', header: 'UF', render: (row) => row.estadoUf },
    { key: 'vendedor', header: 'Vendedor', render: (row) => row.sellerName },
    { key: 'total', header: 'Total', numeric: true, render: (row) => <Money value={row.totalGeral} /> },
  ];

  return (
    <Panel title="Últimas propostas">
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        onRowClick={(row) => onOpen(row.id)}
        empty={
          <EmptyState
            title="Nenhuma proposta ainda."
            description="A primeira leva menos de dois minutos."
            action={
              <Link to="/nova">
                <Button size="sm">Nova proposta</Button>
              </Link>
            }
          />
        }
      />
    </Panel>
  );
}

function TopProdutosPanel({ items }: { items: DashboardTopProduto[] }) {
  return (
    <Panel title="Equipamentos mais vendidos">
      {items.length === 0 ? (
        <EmptyState title="Nada vendido ainda." description="Os equipamentos mais frequentes aparecem aqui." />
      ) : (
        <ol className="painel-list">
          {items.map((item, index) => (
            <li key={item.name} className="painel-list__row">
              <span className="painel-list__rank mono">{index + 1}</span>
              <div className="painel-list__main">
                <span className="painel-list__name">{item.name}</span>
                <span className="painel-list__meta mono">{item.qty} unid.</span>
              </div>
              <div className="painel-list__figures">
                <Money value={item.total} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function ReajustesPanel({ items }: { items: DashboardReajuste[] }) {
  return (
    <Panel title="Reajustes recentes">
      {items.length === 0 ? (
        <EmptyState
          title="Nenhum reajuste ainda."
          description="Quando um preço mudar, a variação aparece aqui."
        />
      ) : (
        <ol className="painel-list">
          {items.map((item, index) => {
            // `deltaPct` é null na primeira versão de preço do produto: não há
            // anterior para comparar.
            const deltaPct = item.deltaPct;
            const tone = deltaTone(deltaPct);
            return (
              <li key={`${item.productName}-${item.validFrom}-${index}`} className="painel-list__row">
                <div className="painel-list__main">
                  <span className="painel-list__name">{item.productName}</span>
                  <span className="painel-list__meta">vigente desde {fmtDate(item.validFrom)}</span>
                </div>
                <div className="painel-list__figures">
                  <Money value={item.unitValue} />
                  {deltaPct === null ? (
                    <span className="faint mono">preço inicial</span>
                  ) : (
                    <Percent value={deltaPct} signed className={toneClass(tone)} />
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}

function PrecosAgendadosPanel({ items }: { items: DashboardPrecoAgendado[] }) {
  return (
    <Panel title="Preços agendados">
      {items.length === 0 ? (
        <EmptyState
          title="Nada agendado."
          description="Reajustes com vigência futura aparecem aqui antes de entrarem em vigor."
        />
      ) : (
        <ol className="painel-list">
          {items.map((item) => (
            <li key={`${item.productId}-${item.validFrom}`} className="painel-list__row">
              <div className="painel-list__main">
                <Link to={`/catalogo/${item.productId}`} className="painel-list__name">
                  {item.productName}
                </Link>
                <span className="painel-list__meta reading-attention">
                  entra em vigor em {fmtDate(item.validFrom)}
                </span>
              </div>
              <div className="painel-list__figures">
                <Money value={item.unitValue} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}
